<script setup lang="ts" generic="T">
import { QSelect, QIcon } from 'quasar'
import { computed, onMounted, ref, useSlots } from 'vue'
import { Color } from '@previo/codebooks'
import type { PValidationRule } from '../../types/types'
import { usePrevioDS } from '../../composables/helpers'
import { useMenuMaxHeight } from '../../composables/menu/useMenuMaxHeight'
import { menuOffset } from './composable/menuOffset'
import InputFieldLabel from './components/LabelInputField.vue'
import { PInputAutofocusType } from './types'

type Props = {
  label?: null | string
  placeholder?: null | string
  hint?: null | string
  rules?: PValidationRule[]
  tooltip?: null | string
  autofocus?: boolean
  autofocusType?: null | PInputAutofocusType
  errorMessage?: null | string
  items: string[]
  noData?: null | string
  disable?: boolean
  readonly?: boolean
  debounce?: null | number
  disableClearable?: boolean
  hideDropdownWhileTyping?: boolean
  cy: string
}

const model = defineModel<string | null>({ default: null })

const slots = useSlots()
const temporaryText = ref<string | null>()

const props = withDefaults(defineProps<Props>(), {
  label: null,
  tooltip: null,
  autofocus: false,
  autofocusType: PInputAutofocusType.INPUT,
  disable: false,
  readonly: false,
  debounce: 0,
  disableClearable: false,
  rules() {
    return []
  },
})

defineEmits(['blur'])

const qSelectComponent = ref<InstanceType<typeof QSelect>>()
const ds = usePrevioDS()
const menuMaxHeight = useMenuMaxHeight(() => qSelectComponent.value?.$el as HTMLElement | undefined)

const isRequired = computed<boolean>(() => {
  return props.rules.includes(ds.rules.required)
})

const isEnableClearable = computed<boolean>(() => {
  if (props.disableClearable || props.readonly || isRequired.value) {
    return false
  }

  return model.value !== null
})

const hasAnyErrors = computed<boolean>(() => {
  return !!props.errorMessage
})

const clear = (): void => {
  model.value = null
  temporaryText.value = null

  // After click in clear icon we don`t want to show popup (Quasar show it automatically)
  setTimeout(() => {
    qSelectComponent.value?.hidePopup()
  }, 1)
}

const createValue = (
  val: string,
  done: (item?: any, mode?: 'add' | 'add-unique' | 'toggle' | undefined) => void,
): void => {
  if (props.disable || props.readonly) {
    return
  }

  if (val.length > 0) {
    model.value = val
    temporaryText.value = null
    done(val, 'add-unique')
  }
}

const onBlur = (): void => {
  if (props.disable || props.readonly) {
    return
  }

  const v = temporaryText.value?.trim()
  if (!v || v === model.value) {
    return
  }

  model.value = v
  temporaryText.value = null

  qSelectComponent.value?.hidePopup()
}

const onFilter = (val: string, update: (callback: () => void) => void): void => {
  temporaryText.value = val ?? ''
  update(() => {})
}

const showPopup = (): void => {
  qSelectComponent.value?.showPopup()
}

onMounted(() => {
  if (props.autofocus && props.autofocusType === PInputAutofocusType.DROPDOWN) {
    showPopup()
  }
})
</script>

<template>
  <input-field-label :label="label" :is-required="isRequired" :tooltip="tooltip" />

  <q-select
    ref="qSelectComponent"
    v-model="model"
    popup-no-route-dismiss
    :rules="rules"
    lazy-rules
    :input-debounce="debounce ?? 0"
    :placeholder="placeholder"
    :hint="hint ?? undefined"
    :options="items"
    :error="hasAnyErrors"
    :error-message="errorMessage ?? undefined"
    :autofocus="autofocus"
    autocomplete="nope"
    :disable="disable"
    :readonly="readonly"
    :class="{ 'ds-input-field--readonly': readonly }"
    :bg-color="Color.WHITE"
    dropdown-icon="fa-light fa-chevron-down"
    clear-icon="fa-light fa-xmark"
    behavior="menu"
    :menu-offset="menuOffset"
    :popup-content-class="menuMaxHeight.popupContentClass.value"
    :popup-content-style="menuMaxHeight.popupContentStyle.value"
    map-options
    outlined
    no-error-icon
    hide-bottom-space
    emit-value
    use-input
    hide-selected
    fill-input
    :data-cy="cy"
    @new-value="createValue"
    @filter="onFilter"
    @blur="onBlur"
    @popup-show="menuMaxHeight.subscribe"
    @popup-hide="menuMaxHeight.unsubscribe"
  >
    <template v-if="slots['options-header']" #before-options>
      <slot name="options-header" />
    </template>

    <template v-if="isEnableClearable" #append>
      <q-icon :color="Color.GREY_400" name="fa-light fa-xmark" @click="clear()" />
    </template>
  </q-select>
</template>
